import React, { useEffect, useState } from 'react';
import { Box, Button, Checkbox, Flex, Radio, RadioGroup, Select, Stack, Text } from '@chakra-ui/react';
import { cqAPI } from '../../lib/Auth';

/**
 * SS-4400: Online Booking › Options › "Collect insurance before the appointment".
 * Saved whole as ols.insurance_collection (the api checks types and ranges and refuses it while
 * the practice's insurance verification is off). Shown only when insurance verification is on.
 */
export const INSURANCE_COLLECTION_DEFAULTS = { enabled: false, channels: 'text', days_before: 3, check_type: 'eligibility', max_asks: 2 };

const DAYS = Array.from({ length: 14 }, (_, i) => i + 1);

const InsuranceCollectionOption = ({ value, credits, bgColor, onSave }) => {
  const [form, setForm] = useState({ ...INSURANCE_COLLECTION_DEFAULTS, ...(value || {}) });
  const [saving, setSaving] = useState(false);
  const [readiness, setReadiness] = useState(null);

  useEffect(() => { setForm({ ...INSURANCE_COLLECTION_DEFAULTS, ...(value || {}) }); }, [value]);

  // how many of the practice's insurers get the full breakdown automatically or need a portal login
  useEffect(() => {
    let alive = true;
    cqAPI.get('/insurance-verification/setup/payers').then((resp) => {
      if (!alive || resp?.type !== 'ok' || !Array.isArray(resp.data?.payers)) return;
      const payers = resp.data.payers.filter(p => p.selected !== false);
      setReadiness({
        automatic: payers.filter(p => p.readiness === 'full_automatic').length,
        login: payers.filter(p => p.readiness === 'full_with_login').length,
      });
    }).catch(() => {});
    return () => { alive = false; };
  }, []);

  const set = (k, v) => setForm(f => ({ ...f, [k]: v }));
  const dirty = JSON.stringify(form) !== JSON.stringify({ ...INSURANCE_COLLECTION_DEFAULTS, ...(value || {}) });
  const save = async () => {
    setSaving(true);
    try {
      await onSave(form);
    } finally {
      setSaving(false);
    }
  };

  const eligibilityCredits = credits?.eligibility ?? 2;
  const breakdownCredits = credits?.breakdown ?? 8;

  return (
    <Box bgColor={bgColor} padding="20px 10px" borderRadius="md" data-testid="insurance-collection-option">
      <Flex align="center" justify="space-between">
        <Text id="insurance-collection-label" minWidth="300px" textAlign="left" fontWeight="semibold">
          Collect insurance before the appointment
        </Text>
        <Checkbox bg="white" aria-labelledby="insurance-collection-label" isChecked={!!form.enabled}
          onChange={(e) => set('enabled', e.target.checked)} />
      </Flex>
      <Text fontSize="sm" color="gray.600" textAlign="left" mt={1}>
        For new patients only: when Viva does not already have valid dental insurance for the visit, it asks the patient and
        verifies what they send. Patients whose insurance checks out are never contacted.
      </Text>
      {form.enabled && (
        <Stack spacing={3} mt={3}>
          <Flex align="center" justify="space-between">
            <Text minWidth="300px" textAlign="left">Ask by</Text>
            <Select bg="white" maxW="260px" aria-label="Ask by" value={form.channels} onChange={(e) => set('channels', e.target.value)}>
              <option value="text">Text</option>
              <option value="call">Call</option>
              <option value="both">Text, then a call a day later</option>
            </Select>
          </Flex>
          <Flex align="center" justify="space-between">
            <Text minWidth="300px" textAlign="left">Days before the appointment</Text>
            <Select bg="white" maxW="260px" aria-label="Days before the appointment" value={form.days_before}
              onChange={(e) => set('days_before', Number(e.target.value))}>
              {DAYS.map(d => <option key={d} value={d}>{d} {d === 1 ? 'day' : 'days'}</option>)}
            </Select>
          </Flex>
          <Flex align="flex-start" justify="space-between">
            <Text minWidth="300px" textAlign="left">Check to run</Text>
            <RadioGroup value={form.check_type} onChange={(v) => set('check_type', v)}>
              <Stack spacing={1} align="flex-start">
                <Radio bg="white" value="eligibility" aria-label="Basic check">
                  Basic check (eligibility) — {eligibilityCredits} credits
                </Radio>
                <Radio bg="white" value="breakdown" aria-label="Full breakdown">
                  Full breakdown — up to {breakdownCredits} credits
                </Radio>
              </Stack>
            </RadioGroup>
          </Flex>
          {form.check_type === 'breakdown' && readiness && (
            <Text fontSize="sm" color="gray.600" textAlign="left" data-testid="insurance-collection-readiness">
              {readiness.automatic} of your insurers get the full breakdown automatically; {readiness.login} need a portal login
              (connect them in Insurance verification setup). Others get the basic check.
            </Text>
          )}
          <Flex align="center" justify="space-between">
            <Text minWidth="300px" textAlign="left">Asks per channel before the office is told</Text>
            <Select bg="white" maxW="260px" aria-label="Asks per channel" value={form.max_asks}
              onChange={(e) => set('max_asks', Number(e.target.value))}>
              {[1, 2, 3].map(n => <option key={n} value={n}>{n}</option>)}
            </Select>
          </Flex>
        </Stack>
      )}
      <Flex justify="flex-end" mt={3}>
        <Button size="sm" colorScheme="blue" isDisabled={!dirty} isLoading={saving} onClick={save}>Save</Button>
      </Flex>
    </Box>
  );
};

export default InsuranceCollectionOption;
